import {assertUnreachable} from '@wandb/weave/common/util/types';
import {QueryResult} from 'react-apollo';

import {
  Exact,
  FetchArtifactCollectionTriggersQuery,
  FetchProjectTriggersQuery,
  TriggeredActionType as GQLTriggeredActionType,
  TriggerScopeType,
} from '../../../../generated/graphql';
import {splitWithMaxSplits} from '../../../../util/string';
import {isUserVisibleTrigger} from '../../../../util/triggers';
import {
  isGenericWebhookTriggeredAction,
  isQueueJobTriggeredAction,
  Trigger,
  TriggeredActionType,
} from './utilTypes';

export const getWebhookActionDetails = (trigger: Trigger) => {
  const emptyDetails = {
    webhook: {
      id: '',
      name: '',
      urlEndpoint: '',
      accessTokenRef: '',
      secretRef: '',
      createdAt: new Date().toISOString(),
    },
    actionConfigString: '{}',
    entityName: '',
  };
  const triggerAction = trigger.triggeredAction;
  if (triggerAction == null) {
    return emptyDetails;
  }
  const actionConfig = triggerAction.actionConfig;
  let actionConfigString = '{}';
  if (isGenericWebhookTriggeredAction(actionConfig)) {
    const webhook = actionConfig.genericWebhook;
    actionConfigString = actionConfig.requestPayload ?? '{}';
    const entityName = actionConfig.entityName ?? '';
    return {webhook, actionConfigString, entityName};
  }
  return emptyDetails;
};

export const getJobActionDetails = (trigger: Trigger) => {
  const emptyDetails = {
    entityName: '',
    projectName: '',
    jobName: {name: '', alias: ''},
    actionConfigString: '{}',
  };
  const triggerAction = trigger.triggeredAction;
  if (triggerAction == null) {
    return emptyDetails;
  }
  const actionConfig = triggerAction.actionConfig;
  let fullJobName = '';
  let actionConfigString = '{}';
  let overrides = {};
  if (isQueueJobTriggeredAction(actionConfig)) {
    const template = actionConfig.template;
    try {
      const parsedActionTemplate = JSON.parse(template);
      fullJobName = parsedActionTemplate.job;
      overrides = parsedActionTemplate.overrides;
      actionConfigString = JSON.stringify(
        parsedActionTemplate.overrides,
        null,
        2
      );
    } catch (e) {
      // If the template parsing errors out, then leave fullJobName as ''
      console.warn(
        'Error parsing QueueJobTriggeredAction template ==> ',
        template
      );
    }
    const [entityName, projectName, jobNameWithAlias] = splitWithMaxSplits(
      fullJobName ?? '',
      '/',
      2
    );
    const [jobName, alias] = jobNameWithAlias?.split(':') ?? [];
    return {
      entityName,
      projectName,
      jobName: {name: jobName, alias},
      actionConfigString,
      overrides,
    };
  }
  return emptyDetails;
};

const ACTION_DISPLAY_NAMES: Record<
  TriggeredActionType | 'NoOpTriggeredAction',
  string
> = {
  // Maps GraphQL typename to display name that's used by existing frontend code.
  QueueJobTriggeredAction: 'Job launch',
  GenericWebhookTriggeredAction: 'Webhook',
  NotificationTriggeredAction: 'Slack notification',
  NoOpTriggeredAction: 'No action',
};

const ACTION_TYPENAMES: Record<
  GQLTriggeredActionType,
  TriggeredActionType | 'NoOpTriggeredAction'
> = {
  // Maps GraphQL `TriggeredActionType` enum name to the GraphQL typename (of the corresponding GraphQL type).
  QUEUE_JOB: 'QueueJobTriggeredAction',
  GENERIC_WEBHOOK: 'GenericWebhookTriggeredAction',
  NOTIFICATION: 'NotificationTriggeredAction',
  NO_OP: 'NoOpTriggeredAction',
};

/**
 * Convert a TriggeredActionType enum name (e.g. 'QUEUE_JOB') to the corresponding GraphQL typename (e.g. 'QueueJobTriggeredAction')
 * @param actionType - The GraphQL TriggeredActionType enum name.
 * @returns The corresponding GraphQL typename.
 */
export const getActionGQLTypename = (actionType: GQLTriggeredActionType) => {
  return ACTION_TYPENAMES[actionType];
};

/**
 * Returns the display name for a TriggeredActionType, if known.
 * @param actionType - The GraphQL typename of the triggered action type.
 * @returns The display name for the triggered action type.
 */
export const getActionDisplayName = (
  actionType: TriggeredActionType | 'NoOpTriggeredAction'
) => {
  return ACTION_DISPLAY_NAMES[actionType];
};

export const getActionInfo = (trigger: Trigger) => {
  const {jobName, actionConfigString: jobConfigString} =
    getJobActionDetails(trigger);
  const {webhook, actionConfigString: webhookConfigString} =
    getWebhookActionDetails(trigger);
  const triggerActionType = trigger.triggeredAction?.actionType;

  let actionConfigString = '{}';
  let actionTypeName = '';
  let actionName = '';
  let configHeader = null;
  switch (triggerActionType) {
    case 'QueueJobTriggeredAction':
      actionTypeName = getActionDisplayName(triggerActionType);
      actionName = jobName.name ?? '';
      actionConfigString = jobConfigString;
      configHeader = 'Overrides';
      break;
    case 'GenericWebhookTriggeredAction':
      actionTypeName = getActionDisplayName(triggerActionType);
      actionName = webhook.name;
      actionConfigString = webhookConfigString;
      configHeader = 'Payload';
      break;
    case 'NotificationTriggeredAction':
      actionTypeName = getActionDisplayName(triggerActionType);
      actionName = 'Slack notification';
      actionConfigString = 'Slack notification';
      configHeader = null;
      break;
    case undefined:
      console.warn('Invalid trigger action type: undefined');
      break;
    default:
      assertUnreachable(triggerActionType);
  }

  return {
    actionTypeName,
    actionName,
    actionConfigString,
    configHeader,
  };
};

/**
 * Convert a the GraphQL typename for a "scopable" GraphQL type (e.g. 'Project') to the corresponding TriggerScopeType enum value (e.g. 'PROJECT')
 * @param typename - The GraphQL typename of the trigger scope type.
 * @returns The corresponding TriggerScopeType enum value.
 */
export const getTriggerScopeType = (
  typename: 'Project' | 'ArtifactSequence' | 'ArtifactPortfolio'
) => {
  switch (typename) {
    case 'Project':
      return TriggerScopeType.Project;
    case 'ArtifactSequence':
    case 'ArtifactPortfolio':
      return TriggerScopeType.ArtifactCollection;
    default:
      assertUnreachable(typename);
  }
};

export const getTriggersDetails = (
  projectTriggersQueryResult: QueryResult<
    FetchProjectTriggersQuery,
    Exact<{
      entityName: string;
      projectName: string;
    }>
  >,
  acTriggersQueryResult?: QueryResult<FetchArtifactCollectionTriggersQuery>,
  actionTypes?: TriggeredActionType[]
) => {
  let projectScopedTriggers =
    projectTriggersQueryResult?.data?.project?.triggers ?? [];
  if (acTriggersQueryResult) {
    projectScopedTriggers = projectScopedTriggers.filter(
      t => t.scope.__typename === 'Project'
    );
  }
  const acScopedTriggers =
    acTriggersQueryResult?.data?.artifactCollection?.triggers.edges.map(t => {
      return t?.node;
    }) ?? [];
  const triggersData = [...acScopedTriggers, ...projectScopedTriggers];

  // triggersData is a list of triggers as gql types
  const triggerDetails: Trigger[] = triggersData
    .map(trigger => {
      const {
        name = '',
        id = '',
        createdAt = new Date().toISOString(),
        enabled = false,
        description = '',
      } = trigger;
      let createdByUser;
      const createdBy = trigger?.createdBy;
      if (createdBy && createdBy.__typename === 'User') {
        createdByUser = {
          username: createdBy.name ?? '',
          id: createdBy.id ?? '',
        };
      }
      const triggerScope = getTriggerScopeType(trigger.scope.__typename);
      const triggerScopeID = trigger.scope.id;
      const triggeringCondition = trigger?.triggeringCondition;
      const triggeringConditionFilterEvent = {
        eventType: triggeringCondition.eventType,
        filter: triggeringCondition.filter,
      };
      const triggerActionType = trigger?.triggeredAction;
      let triggeredAction;

      switch (triggerActionType.__typename) {
        case 'QueueJobTriggeredAction': {
          const triggeredActionQueue = triggerActionType.queue;
          triggeredAction = {
            actionType: 'QueueJobTriggeredAction' as TriggeredActionType,
            actionConfig: {
              queue: {
                ID: triggeredActionQueue?.id ?? '',
                Name: triggeredActionQueue?.name ?? '',
              },
              template: triggerActionType.template,
            },
          };
          break;
        }
        case 'GenericWebhookTriggeredAction': {
          const triggeredActionWebhook = triggerActionType.integration;
          if (
            triggeredActionWebhook.__typename === 'GenericWebhookIntegration'
          ) {
            triggeredAction = {
              actionType:
                'GenericWebhookTriggeredAction' as TriggeredActionType,
              actionConfig: {
                genericWebhook: {
                  id: triggeredActionWebhook.id,
                  name: triggeredActionWebhook.name,
                  urlEndpoint: triggeredActionWebhook.urlEndpoint,
                  accessTokenRef:
                    triggeredActionWebhook.accessTokenRef ?? undefined,
                  secretRef: triggeredActionWebhook.secretRef ?? undefined,
                  createdAt: triggeredActionWebhook.createdAt,
                },
                requestPayload: triggerActionType.requestPayload,
                entityName: triggerActionType.entityName ?? '',
              },
            };
          }
          break;
        }
        case 'NotificationTriggeredAction': {
          const triggeredActionIntegration = triggerActionType.integration;
          if (triggeredActionIntegration.__typename === 'SlackIntegration') {
            triggeredAction = {
              actionType: 'NotificationTriggeredAction' as TriggeredActionType,
              actionConfig: {
                integrationID: triggeredActionIntegration.id,
                entityName: triggerActionType.entityName ?? '',
                channelName: triggeredActionIntegration.channelName ?? '',
              },
            };
          }
          break;
        }
        default:
          console.warn(
            'Invalid trigger action type while parsing triggers for table!'
          );
      }
      return {
        id,
        name,
        createdAt,
        enabled,
        description: description ?? '',
        createdBy: createdByUser,
        triggerScope: {
          scope: triggerScope,
          id: triggerScopeID,
        },
        triggeringCondition: triggeringConditionFilterEvent,
        triggeredAction,
        lastExecutedAt: trigger.triggerExecutions?.edges?.[0]?.node?.createdAt,
      };
    })
    .filter(t => {
      if (!isUserVisibleTrigger(t)) {
        return false;
      }
      if (actionTypes != null) {
        return (
          t.triggeredAction != null &&
          actionTypes.includes(t.triggeredAction.actionType)
        );
      }
      return t;
    });
  return triggerDetails;
};
